"use client";

import type { EmbedTransport, HostToPage } from "@repo/embed";
import { getToolName, isToolUIPart, type UIMessage } from "ai";
import { useCallback, useEffect, useEffectEvent, useMemo, useRef } from "react";
import {
	type BasketDiff,
	type ChannelListener,
	cartMessages,
	deliver,
	envelope,
	hasHost,
	type PageMessageData,
	type PageMessageType,
	parseHostMessage,
	resolveEmbedOrigin,
} from "./bridge";
import { debugLog } from "./debug";
import { BASKET_TOOLS, ESCALATE_TOOL } from "./types";

/**
 * Where the page posts envelope v1 messages to the host. Needs nothing from the session, so the
 * session can tell the host it expired through it. `inline`: the chat is a component of one of
 * our own pages, not a page an app holds, so there is no host: nothing is posted.
 */
export function useBridgePost({
	allowedOrigins,
	parentOrigin,
	embed,
	inline = false,
}: {
	allowedOrigins: readonly string[];
	parentOrigin: string | undefined;
	embed: EmbedTransport | undefined;
	inline?: boolean;
}) {
	// Resolved on first use, in the browser: ancestorOrigins and referrer are not on the server.
	const origin = useRef<string | null | undefined>(undefined);
	const embedOrigin = useCallback(() => {
		if (inline) {
			return null;
		}
		if (origin.current === undefined) {
			origin.current = resolveEmbedOrigin(allowedOrigins, parentOrigin);
		}
		return origin.current;
	}, [inline, allowedOrigins, parentOrigin]);

	/** One message to the host; false when there is none to receive it. */
	const send = useCallback(
		(message: object, options?: { string?: boolean }) =>
			!inline && deliver(message, embed, embedOrigin(), options),
		[inline, embed, embedOrigin],
	);

	const post = useCallback(
		<T extends PageMessageType>(
			type: T,
			data: PageMessageData<T>,
			id?: string,
		) => send(envelope(type, data, id)),
		[send],
	);

	return useMemo(
		() => ({ embed, embedOrigin, inline, send, post }),
		[embed, embedOrigin, inline, send, post],
	);
}

export type BridgePost = ReturnType<typeof useBridgePost>;

/**
 * The page's side of the bridge (ADR-5.4): posts envelope v1 messages to the host (`out`) and
 * takes HostToPage messages from `window.hephon.receive` (webviews), `message` events on
 * Android's `HephonBridge` (its reply proxy), or postMessage (iframes, only from the embedding
 * origin and the parent window). Cart events come from the server's basket diffs, each
 * emitted once.
 */
export function useHostBridge({
	out,
	messages,
	conversationId,
	onMessage,
	onHandoff,
}: {
	out: BridgePost;
	/** The chat, scanned for basket and handoff tool results as they arrive. */
	messages: UIMessage[];
	conversationId: () => string | null;
	onMessage: (message: HostToPage) => void;
	/**
	 * The agent asked for a person (escalate_to_human): the page shows that one is joining.
	 * Pass a stable function: `toolResult` is rebuilt when it changes.
	 */
	onHandoff: () => void;
}) {
	const { embed, embedOrigin, inline, send, post } = out;
	const handle = useEffectEvent(onMessage);
	useEffect(() => {
		// Inline in one of our pages, nobody is around to send anything, and the page's own
		// globals stay its own.
		if (inline) {
			return;
		}
		const receive = (raw: unknown) => {
			const message = parseHostMessage(raw);
			debugLog(
				"<-",
				message
					? `${message.type} ${message.id}`
					: "ignored: not a v1 host message",
			);
			if (message) {
				handle(message);
			}
		};
		window.hephon = { receive };
		// Android: the SDK answers through the reply proxy of the HephonBridge object.
		const channel = window.HephonBridge;
		const onChannelMessage: ChannelListener = (event) =>
			receive(event.data);
		channel?.addEventListener?.("message", onChannelMessage);
		const onWindowMessage = (event: MessageEvent) => {
			const from = embedOrigin();
			if (
				from &&
				event.origin === from &&
				event.source === window.parent
			) {
				receive(event.data);
			}
		};
		window.addEventListener("message", onWindowMessage);
		return () => {
			window.removeEventListener("message", onWindowMessage);
			channel?.removeEventListener?.("message", onChannelMessage);
			delete window.hephon;
		};
	}, [inline, embedOrigin]);

	// The page's height, for the retailer's site: the widget loader re-dispatches it as
	// `hephon:resize` and does not size its panel with it (the panel keeps its CSS size).
	useEffect(() => {
		if (embed !== "widget") {
			return;
		}
		let last = -1;
		const observer = new ResizeObserver(() => {
			const height = Math.ceil(document.documentElement.scrollHeight);
			if (height !== last) {
				last = height;
				post("resize", { height });
			}
		});
		observer.observe(document.documentElement);
		return () => observer.disconnect();
	}, [embed, post]);

	// Keys typed in the chat never reach the retailer's page, so Escape closes the widget
	// from here. An open sheet or menu takes the Escape first.
	useEffect(() => {
		if (embed !== "widget") {
			return;
		}
		const onKeyDown = (event: KeyboardEvent) => {
			if (
				event.key === "Escape" &&
				!event.defaultPrevented &&
				!document.querySelector('[role="dialog"], [role="menu"]')
			) {
				post("close", {});
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [embed, post]);

	/** Bridge events for one basket diff (steppers, host snapshot, tool result, call). */
	const cart = useCallback(
		(diff: BasketDiff, origin: "agent" | "shopper" | "host") => {
			const id = conversationId();
			if (!id) {
				return;
			}
			for (const message of cartMessages(diff, {
				conversationId: id,
				origin,
			})) {
				send(message);
			}
		},
		[conversationId, send],
	);

	/**
	 * A finished tool call, from the chat stream or a live call. Each call id is emitted once:
	 * a reload restores old results without replaying their events.
	 */
	const emitted = useRef(new Set<string>());
	const toolResult = useCallback(
		(toolCallId: string, toolName: string, output: unknown) => {
			if (emitted.current.has(toolCallId)) {
				return;
			}
			emitted.current.add(toolCallId);
			if (BASKET_TOOLS.has(toolName)) {
				cart(output as BasketDiff, "agent");
			} else if (toolName === ESCALATE_TOOL) {
				const id = conversationId();
				const result = output as {
					escalated?: boolean;
					handoff?: boolean;
					reason?: string;
				};
				if (id && (result?.escalated || result?.handoff)) {
					onHandoff();
					post("handoff", {
						conversationId: id,
						...(result.reason ? { reason: result.reason } : {}),
					});
				}
			}
		},
		[cart, conversationId, post, onHandoff],
	);

	/** Restored history: its tool calls were announced when they ran, so none replays. */
	const restored = useCallback((history: UIMessage[]) => {
		for (const message of history) {
			for (const part of message.parts) {
				if (isToolUIPart(part)) {
					emitted.current.add(part.toolCallId);
				}
			}
		}
	}, []);

	// Any finished call not announced yet. Not "seen running first": the chat renders at most
	// every 50 ms, so a call that starts and ends inside one frame is first seen finished.
	useEffect(() => {
		const latest = messages.at(-1);
		if (latest?.role !== "assistant") {
			return;
		}
		for (const part of latest.parts) {
			if (isToolUIPart(part) && part.state === "output-available") {
				toolResult(part.toolCallId, getToolName(part), part.output);
			}
		}
	}, [messages, toolResult]);

	return useMemo(
		() => ({
			post,
			cart,
			toolResult,
			restored,
			/** Whether an app is around the page to take the cart at checkout. */
			embedded: () => !inline && hasHost(embed, embedOrigin()),
			/** The pre-v1 cart message, posted next to `checkout` for one release. */
			postLegacy: (message: object) => send(message, { string: true }),
		}),
		[post, cart, toolResult, restored, inline, send, embed, embedOrigin],
	);
}
